import React, { forwardRef, useContext } from 'react';

import {
  AppContainerContext,
  Comp,
  Flexbox,
  getContainerProps,
  isComponent,
  partitionComponents,
  useTheme,
  warnOnUnknownChildren,
} from '@redsift/design-system';
import { MenuButtonContentProps } from './types';
import {
  FloatingPortal,
  PopoverContent,
  StyledPopoverContent,
  useMergeRefs,
  usePopoverContext,
} from '@redsift/popovers';
import classNames from 'classnames';
import { MenuButtonContentFooter } from '../menu-button-content-footer';
import { MenuButtonContentHeader } from '../menu-button-content-header';
import { MenuButtonContentMenu } from '../menu-button-content-menu';

const COMPONENT_NAME = 'MenuButtonContent';
const CLASSNAME = 'redsift-menu-button-content';
const ALLOWED_CHILDREN = new Set(['MenuButtonContentHeader', 'MenuButtonContentMenu', 'MenuButtonContentFooter']);

/**
 * The MenuButtonContent component.
 */
export const BaseMenuButtonContent: Comp<MenuButtonContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, style } = props;
  const containerProps = getContainerProps(props);

  const { getFloatingProps, isOpen, refs, strategy, x, y, hideInsteadOfClose } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const appContainerState = useContext(AppContainerContext);
  const theme = useTheme();

  if (process.env.NODE_ENV !== 'production') {
    warnOnUnknownChildren(children, 'MenuButtonContent', ALLOWED_CHILDREN);
  }

  const [[header], [menu], [footer]] = partitionComponents(React.Children.toArray(children), [
    isComponent('MenuButtonContentHeader'),
    isComponent('MenuButtonContentMenu'),
    isComponent('MenuButtonContentFooter'),
  ]);

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen || hideInsteadOfClose ? (
        <StyledPopoverContent
          $theme={theme!}
          ref={popoverRef}
          {...getFloatingProps(props)}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            display: hideInsteadOfClose && !isOpen ? 'none' : 'flex',
            ...style,
          }}
          className={classNames(PopoverContent.className, BaseMenuButtonContent.className, className)}
        >
          {!header && !menu && !footer ? (
            <Flexbox flexDirection="column" gap="0px" width="100%" {...containerProps}>
              <MenuButtonContentMenu>{children}</MenuButtonContentMenu>
            </Flexbox>
          ) : (
            <Flexbox flexDirection="column" gap="0px" width="100%" {...containerProps}>
              {header}
              {menu}
              {footer}
            </Flexbox>
          )}
        </StyledPopoverContent>
      ) : null}
    </FloatingPortal>
  );
});
BaseMenuButtonContent.className = CLASSNAME;
BaseMenuButtonContent.displayName = COMPONENT_NAME;

export const MenuButtonContent = Object.assign(BaseMenuButtonContent, {
  Header: MenuButtonContentHeader,
  Menu: MenuButtonContentMenu,
  Footer: MenuButtonContentFooter,
});
